import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { ColumnDef, FilterFn } from '@tanstack/react-table';

import useFundContext from '@/contexts/Fund/useFundContext';
import { KollelDtoIn } from '@/models/in/KollelDtoIn';
import { getKollelStatusColor, getKollelStatusLabelKey } from '@/helpers/kollelStatus';
import DataTable from '@/components/ui/DataTable/DataTable';
import { Col } from '@/components/ui/DataTable/ColumnsHelpers';
import { useGetAuthApi } from '@/hooks/UseApiCalls';
import Button from '@/components/ui/Button';
import SearchInput from '@/components/ui/SearchInput/SearchInput';
import KollelFormDialog from './KollelFormDialog';
import KollelFoldersDialog from './KollelFoldersDialog';
import NewKollelDialog from '@/pages/newKollel/NewKollelDialog';

const kollelSearch: FilterFn<KollelDtoIn> = (row, _columnId, value) => {
  if (!value) return true;
  const searchValue = String(value).toLowerCase();
  const { name, notes } = row.original;
  return [name, notes].some((field) => field?.toLowerCase().includes(searchValue));
};

/**
 * The fund's kollels list ("רשימת כוללים", HAT-1446 phase 1-6) - columns follow the designer's
 * spec (node 5238:46273): שם הכולל | אברכים מקושרים | סטטוס | הערות, plus a "תיקיות בקשות" action
 * button (phase 6) opening KollelFoldersDialog. "בקשות פתוחות" (an open-requests count) is still
 * not wired - it would need a dedicated query across SupportRequests, not just the folders
 * themselves, so it stays deferred rather than shown with a misleading zero.
 *
 * "+ הוספה" opens the full wizard (NewKollelDialog, phase 3); clicking a row still opens the
 * simpler flat KollelFormDialog (phase 2) for viewing/editing an existing kollel, matching the
 * Figma's own split between the multi-step creation flow and the flat "עריכת תיקייה" edit panel.
 *
 * "תיקיות בקשות" (HAT-1446 phase 6) opens KollelFoldersDialog - a separate button rather than part
 * of the row click, since row click already opens the edit form.
 */
const KollelsPage = () => {
  const { t } = useTranslation();
  const { currentFund } = useFundContext();

  const [searchQuery, setSearchQuery] = useState('');
  const [isNewKollelOpen, setIsNewKollelOpen] = useState(false);
  const [selectedKollel, setSelectedKollel] = useState<KollelDtoIn | null>(null);
  const [foldersKollel, setFoldersKollel] = useState<KollelDtoIn | null>(null);

  const { data: kollels, isLoading, refetch } = useGetAuthApi<KollelDtoIn[]>(
    currentFund?.id ? `funds/${currentFund.id}/kollels` : '',
  );

  const columns: ColumnDef<KollelDtoIn>[] = [
    {
      id: 'name',
      header: Col(t('kollels.list.name')),
      accessorKey: 'name',
      enableSorting: true,
      meta: { className: 'min-w-[220px]' },
    },
    {
      id: 'avrechs',
      header: Col(t('kollels.list.avrechs')),
      accessorFn: (row) => row.avrechs?.length ?? 0,
      enableSorting: true,
      cell: ({ row }) => <span>{t('kollels.list.avrechsCount', { count: row.original.avrechs?.length ?? 0 })}</span>,
      meta: { className: 'min-w-[140px]' },
    },
    {
      id: 'status',
      header: Col(t('kollels.list.status')),
      accessorKey: 'status',
      enableSorting: true,
      cell: ({ row }) => (
        <div className="flex items-center gap-2">
          <div
            className="w-[10px] h-[10px] rounded-full shrink-0"
            style={{ backgroundColor: getKollelStatusColor(row.original.status) }}
          />
          <span>{t(getKollelStatusLabelKey(row.original.status))}</span>
        </div>
      ),
      meta: { className: 'min-w-[150px]' },
    },
    {
      id: 'comments',
      header: Col(t('kollels.list.comments')),
      accessorKey: 'notes',
      enableSorting: true,
      cell: ({ row }) => (
        <span className={row.original.notes ? '' : 'text-(--font-tertiary)'}>{row.original.notes || '—'}</span>
      ),
      meta: { className: 'min-w-[180px]' },
    },
    {
      id: 'folders',
      header: Col(''),
      enableSorting: false,
      cell: ({ row }) => (
        <Button
          variant="outline"
          onClick={(e) => {
            e.stopPropagation();
            setFoldersKollel(row.original);
          }}
        >
          {t('kollels.list.folders')}
        </Button>
      ),
      meta: { className: 'min-w-[140px]' },
    },
  ];

  return (
    <div>
      <DataTable
        title={t('kollels.list.titleWithCount', { count: kollels?.length ?? 0 })}
        data={kollels ?? []}
        isLoading={isLoading}
        columns={columns}
        filters={
          <div className="flex items-center justify-between gap-3">
            <Button variant="primary" onClick={() => setIsNewKollelOpen(true)}>
              {t('kollels.list.add')}
            </Button>
            <SearchInput value={searchQuery} onChange={setSearchQuery} placeholder="kollels.list.search" />
          </div>
        }
        emptyStateContent={{ title: t('kollels.list.noData'), description: t('kollels.list.noDataDescription') }}
        searchQuery={searchQuery}
        setSearchQuery={setSearchQuery}
        customGlobalSearch={kollelSearch}
        handleRowClick={setSelectedKollel}
      />

      <NewKollelDialog
        fundId={currentFund?.id}
        open={isNewKollelOpen}
        onClose={() => setIsNewKollelOpen(false)}
        onSuccess={refetch}
      />

      {selectedKollel && (
        <KollelFormDialog
          fundId={currentFund?.id}
          kollel={selectedKollel}
          open
          onClose={() => setSelectedKollel(null)}
          onSaved={refetch}
        />
      )}

      {foldersKollel && (
        <KollelFoldersDialog
          fundId={currentFund?.id}
          kollel={foldersKollel}
          open
          onClose={() => setFoldersKollel(null)}
        />
      )}
    </div>
  );
};

export default KollelsPage;
